import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PARTNER_LABELS } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle } from "lucide-react";

export default function Settings() {
    const { user, canWrite } = useAuth();
    const [mapping, setMapping] = useState({ bashari: [], ido: [] });
    const [resetOpen, setResetOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [password, setPassword] = useState("");
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        api.get("/auth/partner-mapping").then(r => setMapping(r.data)).catch(() => {});
    }, []);

    const doReset = async () => {
        if (confirmText !== "RESET") return;
        if (!password) { toast.error("Enter the admin password"); return; }
        setResetting(true);
        try {
            const { data } = await api.post("/admin/reset", { confirm: "RESET", password });
            const total = Object.values(data.deleted || {}).reduce((s, n) => s + n, 0);
            toast.success(`Reset complete. Deleted ${total} records across ${Object.keys(data.deleted).length} collections.`);
            setResetOpen(false); setConfirmText(""); setPassword("");
            // hard reload to clear any cached state
            setTimeout(() => { window.location.href = "/"; }, 500);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        } finally { setResetting(false); }
    };

    const partner = user?.partner_key;

    return (
        <div className="max-w-2xl space-y-8 animate-fade-up" data-testid="settings-page">
            <header>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Account</div>
                <h1 className="font-display text-4xl leading-none tracking-tight">Settings</h1>
            </header>

            <section className="border border-stone-200 bg-white p-6 space-y-4" data-testid="settings-account">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Signed in as</div>
                    <div className="text-stone-900 mt-1">{user?.name || user?.email}</div>
                    <div className="text-xs text-stone-500">{user?.email}</div>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Partner role</div>
                    {partner ? (
                        <div className="mt-1 inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-50 text-emerald-800 px-3 py-1.5 text-sm" data-testid="settings-partner-badge">
                            {PARTNER_LABELS[partner]}
                        </div>
                    ) : (
                        <div className="mt-1 inline-flex items-center gap-2 border border-stone-300 bg-stone-100 text-stone-600 px-3 py-1.5 text-sm" data-testid="settings-partner-badge">
                            Viewer (no partner role)
                        </div>
                    )}
                    <p className="text-xs text-stone-500 mt-2">
                        Partner mapping is configured by the app administrator via environment variables and cannot be changed from the UI.
                    </p>
                </div>
            </section>

            <section className="border border-stone-200 bg-white p-6 space-y-3" data-testid="settings-partner-mapping">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Current partner mapping</div>
                {["bashari", "ido"].map(pk => (
                    <div key={pk} className="flex items-baseline gap-3">
                        <div className="w-20 text-sm font-medium text-stone-900">{PARTNER_LABELS[pk]}</div>
                        <div className="flex-1 text-xs text-stone-600 font-mono" data-testid={`mapping-${pk}`}>
                            {(mapping[pk] || []).length ? mapping[pk].join(", ") : <span className="text-stone-400">— none configured —</span>}
                        </div>
                    </div>
                ))}
            </section>

            {canWrite && (
                <section className="border border-rose-300 bg-rose-50/40 p-6 space-y-4" data-testid="settings-danger-zone">
                    <div className="flex items-center gap-2 text-rose-700">
                        <AlertTriangle className="h-4 w-4" strokeWidth={1.5} />
                        <div className="text-[10px] uppercase tracking-widest">Danger zone</div>
                    </div>
                    <div>
                        <div className="text-sm text-stone-900 font-medium">Reset all business data</div>
                        <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                            Permanently deletes every parcel, sub-parcel, jewelry, expense, sale and uploaded file. Users and sessions are kept. This action cannot be undone.
                        </p>
                    </div>

                    <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) { setConfirmText(""); setPassword(""); } }}>
                        <DialogTrigger asChild>
                            <Button className="rounded-none bg-rose-600 hover:bg-rose-500 text-white" data-testid="open-reset-dialog">
                                Reset all business data…
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-none bg-[#faf7ef] border border-rose-300">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl text-rose-700">Are you absolutely sure?</DialogTitle>
                                <DialogDescription className="text-stone-500">
                                    Wipes every parcel, jewelry, expense, sale and uploaded file. Users and login sessions stay intact. This action is irreversible.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3 text-sm text-stone-700">
                                <p>This will permanently delete <span className="font-semibold">all parcels, jewelry, expenses, sales and uploaded files</span> on <span className="font-mono">{window.location.hostname}</span>.</p>
                                <div>
                                    <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Admin password</label>
                                    <Input
                                        type="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder="Enter the admin password"
                                        className="rounded-none bg-white border-rose-300"
                                        data-testid="reset-password-input"
                                        autoFocus
                                        autoComplete="current-password"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Type <span className="font-mono font-bold text-rose-700">RESET</span> to confirm</label>
                                    <Input
                                        value={confirmText}
                                        onChange={e => setConfirmText(e.target.value)}
                                        placeholder="Type RESET"
                                        className="rounded-none bg-white border-rose-300 font-mono"
                                        data-testid="reset-confirm-input"
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setResetOpen(false)} className="rounded-none" data-testid="reset-cancel">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={doReset}
                                    disabled={confirmText !== "RESET" || !password || resetting}
                                    className="rounded-none bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-40"
                                    data-testid="reset-confirm-submit"
                                >
                                    {resetting ? "Deleting…" : "Delete everything"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </section>
            )}
        </div>
    );
}
